/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import {
    func, object, string, bool,
} from 'prop-types';
import { useLocation, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';

import StyledButton from '../../../../../EnterpriseDesignSystem/Button/StyledButton';
import { trackEvent } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomerIdentificationEvent, emitCustomTrackingEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { setOfferInboxData, setOfferInboxOffers } from '../../../../../../../state/ducks/App/App-Actions';
import { getOfferInboxOfferPayloadById, getPageType } from '../../../../../../../state/ducks/App/App-Selectors';
import noop from '../../../../../../helpers/noop';
import { getIsAuthenticatedStatus, getProfileInfo } from '../../../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import {
    OFFER_INBOX_EMAIL_OPT_IN, OFFER_INBOX_SEE_MORE_ITEM, buildOfferInboxBannerEvent, getCmsBannerFields, getOfferPromoCode,
} from '../offerInboxTracking';

const useStyles = makeStyles((theme) => ({
    button: (styles) => ({
        margin: styles?.margin || '',
    }),
}));

const OfferButton = ({
    buttonData,
    className,
    trackClickThrough,
    offer,
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const classes = useStyles({ margin: buttonData?.margin });
    const pageType = useSelector(getPageType);
    const isAuthenticated = useSelector(getIsAuthenticatedStatus);
    const userInfo = useSelector(getProfileInfo);
    const isOfferGateway = offer?.type === 'offer-gateway';
    const offerUID = isOfferGateway ? offer?.content?.uid : offer?.uid;
    const offerData = useSelector((state) => getOfferInboxOfferPayloadById(state, offerUID));

    const offerDetails = offer?.value?.value?.offerGatewayRecipe || {};

    const isOfferUnlocked = offerDetails?.offerFlow === 'Offer Unlocked' || offerDetails.offerFlow === 'Offer Unlocked - SMS';
    const isEmailOptin = offerDetails.offerFlow === 'Optin only' && offerDetails.offerOptinType === 'Email';
    const inputFormId = offer?.content?.input_form_id;

    const buttonType = buttonData?.button_type;
    let url = buttonData?.cta_button?.link?.href;
    const backgroundColor = buttonData?.background_color?.color;

    if (buttonType === 'click' && (offerDetails?.offerEndState === 'Collection Page' && offerDetails?.collectionURL)) {
        if (offerDetails?.offerBannerCode) {
            url = `${offerDetails.collectionURL}?r=${offerDetails.offerBannerCode}`;
        } else {
            url = offerDetails.collectionURL;
        }
    }

    const addQueryParameter = () => {
        if (offerDetails?.offerEndState === 'Promo Code' && offerDetails?.offerBannerCode) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('r', offerDetails.offerBannerCode);
            history.push({
                pathname: location.pathname,
                search: searchParams.toString(),
            });
        }
    };

    const getBannerFields = () => {
        const promoCode = getOfferPromoCode(offer);

        if (buttonData?.cta_button?.launch_modal) {
            return { bannerItemName: OFFER_INBOX_SEE_MORE_ITEM };
        }
        if (isOfferGateway && offerDetails?.offerEndState === 'Promo Code' && promoCode) {
            return { bannerItemName: promoCode };
        }
        if (isOfferGateway && isEmailOptin && isAuthenticated && buttonType === 'click') {
            return OFFER_INBOX_EMAIL_OPT_IN;
        }

        const cmsFields = getCmsBannerFields(buttonData?.cta_button?.tracking);
        return {
            ...cmsFields,
            bannerItemName: cmsFields.bannerItemName || buttonData?.cta_button?.link?.title,
        };
    };

    const handleClick = () => {
        // form submit buttons are tracked as opt-ins by InputField once the input validates
        const isFormSubmit = inputFormId && buttonData?.cta_button?.type === 'submit';

        if (!isFormSubmit) {
            dispatch(trackEvent(buildOfferInboxBannerEvent({
                ...getBannerFields(),
                clickText: buttonData?.cta_button?.link?.title,
                clickUrl: url,
            })));
        }

        trackClickThrough();

        if (!isOfferGateway) {
            dispatch(emitCustomTrackingEvent({
                action: 'Click',
                eventSubType: 'feature-tracking',
                page: { type: pageType || 'home' },
                label: 'offer-inbox-todays-offer',
            }));
        }

        if (buttonData?.cta_button?.disabled || buttonData?.cta_button?.link?.href) return;

        if (isOfferGateway) {
            const buttonId = buttonData?.button_id;
            if (buttonId) {
                const offerPayload = {
                    offerId: offerUID,
                    offer: {
                        buttonState: {
                            ...offerData?.buttonState,
                            [buttonId]: offerData?.buttonState?.[buttonId] + 1,
                        },
                    },
                };
                dispatch(setOfferInboxOffers(offerPayload));
            }

            if (isEmailOptin && isAuthenticated && buttonData?.button_type === 'click') {
                dispatch(emitCustomerIdentificationEvent({
                    customEvent: {
                        eventDefinitionKey: offerDetails?.eventDefinitionKey,
                        offerName: offerDetails?.offerName,
                        offerDesc: offerDetails?.offerDesc,
                        offerPercent: offerDetails?.offerPercent,
                    },
                    page: pageType || 'home',
                    eventType: 'clickstream.customer-identification',
                    eventSubType: 'journey-email-trigger',
                    user: {
                        email: userInfo?.email,
                    },
                }));

                dispatch(setOfferInboxData({
                    offerGatewayOptin: true,
                    email: userInfo?.email,
                }));
            }

            // when button_type is 'click', functionality should depend on salesforce response
            if (buttonData?.button_type === 'click' && isOfferUnlocked) {
                addQueryParameter();
            }

            if (buttonData?.button_type === 'reset_sms') {
                dispatch(setOfferInboxData({
                    offerGatewayOptinSMS: false,
                    phoneNumberSuccess: false,
                }));
            }
        }
    };

    // Check if button should be rendered based on button_state
    if (buttonData?.button_state !== null && buttonData?.button_state !== offerData?.buttonState?.[buttonData?.button_id]) {
        return null;
    }

    return (
        <div className={`${className} ${classes.button}`}>
            <StyledButton
                link={url}
                label={buttonData?.cta_button?.link?.title}
                variant={buttonData?.cta_button?.variant}
                size={buttonData?.cta_button?.size}
                fullWidth={buttonData?.cta_button?.full_width}
                disabled={buttonData?.cta_button?.disabled}
                type={buttonData?.cta_button?.type}
                attributes={{
                    form: inputFormId || '',
                    style: { 'background-color': backgroundColor },
                }}
                icon={buttonData?.icon || {}}
                handleOnClick={handleClick}
            />
        </div>
    );
};

OfferButton.propTypes = {
    buttonData: object.isRequired,
    className: string,
    trackClickThrough: func,
    offer: object,
};

OfferButton.defaultProps = {
    className: '',
    trackClickThrough: noop,
    offer: {},
};

export default OfferButton;
